import { useEffect, useMemo, useState } from 'react';
import { useLocation, useMatch, useParams } from 'react-router';

import CommentsPanel from '@/components/comments/CommentsPanel';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { qualityMachine, qualityMachineConfig } from '@/config/qualityStates';
import { QualityDetailContext } from '@/contexts/QualityContext';
import useCognitoUsers from '@/hooks/useCognitoUsers';
import { DETAIL_MODE, DETAIL_TYPE, useDetailMode } from '@/hooks/useDetailMode';
import { normalizeQuality, useQuality } from '@/hooks/useQualitiesAPI';
import PageLayout from '@/layouts/PageLayout';
import { getUserInfo } from '@/lib/auth';
import { apiService } from '@/lib/axios';
import QualityApprovalsView from '@/views/qualities/QualityApprovalsView';
import QualityInformationView from '@/views/qualities/QualityInformationView';
import QualityNonconformityView from '@/views/qualities/QualityNonconformityView';
import QualityPlanView from '@/views/qualities/QualityPlanView';

import {
  QualityDetailActionsHead,
  QualityDetailActionsTail,
} from './QualityActions';

const DEFAULT_FORM = {
  informationDescription: '',
  informationDetails: '',
  informationFile: '',
  informationRemark: '',
  nonconformitySpecification: {},
  nonconformityQms: {},
  nonconformityRegulations: {},
  nonconformitySafety: {},
  nonconformityDetails: {
    statement: { text: '', attachments: [], pendingAttachments: [] },
    rootCause: { text: '' },
  },
  planActionReason: '',
  planChecked: '1',
  planActions: [],
  planECReason: '',
  planRequired: '1',
  planCriteriaEffectiveness: '',
  planCriteriaQA: '',
  planFile: '',
  preparedBy: '',
  serial: '',
  requestDate: '',
  reviewers: {
    managementApprovers: null,
    processOwners: null,
    qmrQa: null,
  },
};

const COMMENT_TABS = [
  {
    key: 'information',
    title: 'Comments for Quality Event Information',
  },
  {
    key: 'nonconformity',
    title: 'Comments for Nonconformity Identification',
  },
  {
    key: 'plan',
    title: 'Comments for Action Plan',
  },
];

const QualityDetailPage = () => {
  const { id } = useParams();
  const location = useLocation();
  const [form, setForm] = useState(DEFAULT_FORM);
  const [activeTab, setActiveTab] = useState('information');
  const [reviews, setReviews] = useState([]);
  const [canApprove, setCanApprove] = useState(false);

  // ── Cognito users for designation lookup ──────
  const { users: cognitoUsers = [] } = useCognitoUsers();
  const usersMap = useMemo(
    () => Object.fromEntries(cognitoUsers.map((u) => [u.sub, u])),
    [cognitoUsers]
  );

  const { isSnapshotMode, isMasterPreview, mode } = useDetailMode(
    DETAIL_TYPE.QUALITY
  );
  const snapshotData = location.state?.snapshot || null;
  const snapshotComments = location.state?.comments;
  const { quality: currentQuality } = useQuality(
    isSnapshotMode ? null : id,
    { usersMap }
  );
  const quality = useMemo(
    () =>
      isSnapshotMode && snapshotData
        ? normalizeQuality(snapshotData, usersMap)
        : currentQuality,
    [currentQuality, isSnapshotMode, snapshotData, usersMap]
  );

  const reviewerRole = useMemo(() => {
    const reviewGroupOrder = qualityMachineConfig.reviewGroupOrder || [];
    const reviewers = quality?.reviewers;
    const currentActorIndex = quality?.currentActorIndex ?? 0;

    if (!reviewers || !reviewGroupOrder.length) return null;

    const currentUserSub = getUserInfo()?.sub;

    if (!currentUserSub) return null;

    const pipeline =
      quality?.reviewerPipeline ||
      reviewGroupOrder.flatMap((group) => reviewers[group] || []);
    const current = pipeline[currentActorIndex];
    const currentSub =
      typeof current === 'object'
        ? current?.sub || current?.value?.sub
        : current;

    if (currentUserSub !== currentSub) return null;

    let index = 0;

    for (const group of reviewGroupOrder) {
      const groupMembers = reviewers[group] || [];

      if (
        currentActorIndex >= index &&
        currentActorIndex < index + groupMembers.length
      ) {
        const roleMap = {
          processOwners: 'Process Owner',
          qmrQa: 'QA',
          managementApprovers: 'MGMT',
        };

        return roleMap[group] || group;
      }

      index += groupMembers.length;
    }

    return null;
  }, [
    quality?.reviewers,
    quality?.reviewerPipeline,
    quality?.currentActorIndex,
  ]);

  const matchNew = useMatch('/quality/new');

  const setFormByKey = (key, value) => {
    setForm((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  useEffect(() => {
    setForm(
      matchNew || !quality ? DEFAULT_FORM : { ...DEFAULT_FORM, ...quality }
    );
  }, [quality, matchNew]);

  useEffect(() => {
    if (isSnapshotMode) {
      setReviews(
        (snapshotComments || []).map((comment) => ({
          ...comment,
          updateAt: comment.updateAt || comment.updatedAt,
        }))
      );
      return;
    }

    if (!id || mode !== DETAIL_MODE.STATIC) return;

    apiService.comments
      .getAll({
        entityType: 'QualityEvent',
        entityId: id,
      })
      .then((res) => {
        if (!res.data.success) return;

        const loadedReviews = res.data.data.map((comment) => ({
          id: comment.id,
          tab: comment.tab,
          text: comment.text,
          updateAt: comment.updatedAt,
          entityType: comment.entityType,
          entityId: comment.entityId,
          entityRevision: comment.entityRevision,
          resolvedAt: comment.resolvedAt,
          frozenAt: comment.frozenAt,
          authorSub: comment.authorSub,
          authorName: comment.authorName,
        }));

        setReviews((prev) => {
          const savedIds = new Set(loadedReviews.map((review) => review.id));

          return [
            ...loadedReviews,
            ...prev.filter((review) => !savedIds.has(review.id)),
          ];
        });
      })
      .catch(() => {});
  }, [id, mode, isSnapshotMode, snapshotComments]);

  const showCommentsPanel =
    activeTab !== 'approvals' &&
    activeTab !== 'summary' &&
    (mode === DETAIL_MODE.STATIC ||
      (mode === DETAIL_MODE.EDIT && quality?.status === 'RETURNED')) &&
    !isMasterPreview &&
    (!reviewerRole ? reviews.some((review) => review.tab === activeTab) : true);
  const blockingCommentsCount = useMemo(() => {
    const currentSub = getUserInfo()?.sub;

    return reviews.filter(
      (review) => !review.resolvedAt && review.authorSub === currentSub
    ).length;
  }, [reviews]);
  const allowedActions = useMemo(() => {
    const userInfo = getUserInfo();
    const reviewerPipeline =
      quality?.reviewerPipeline ||
      qualityMachine.buildPipeline(quality?.reviewers);

    return !isSnapshotMode && quality?.status
      ? qualityMachine.getActions(
          quality.status,
          userInfo?.sub,
          reviewerPipeline,
          quality.currentActorIndex ?? 0,
          quality.creator || quality.requestorId,
          userInfo?.['cognito:groups']
        )
      : [];
  }, [
    quality?.creator,
    quality?.currentActorIndex,
    quality?.requestorId,
    quality?.reviewerPipeline,
    quality?.reviewers,
    quality?.status,
    isSnapshotMode,
  ]);

  return (
    <QualityDetailContext.Provider
      value={{
        activeTab,
        form,
        id,
        mode,
        reviews,
        setActiveTab,
        setFormByKey,
      }}
    >
      <PageLayout
        subtitle={
          !matchNew && `Preview QM Event: ${form.serial || form.id}`
        }
        title="Quality Event Reporting"
        toolbarHead={isSnapshotMode ? null : <QualityDetailActionsHead />}
        toolbarTail={
          !isSnapshotMode && <QualityDetailActionsTail
            allowedActions={allowedActions}
            blockingCommentsCount={blockingCommentsCount}
            canApprove={canApprove}
            reviews={reviews}
          />
        }
      >
        {isSnapshotMode && (
          <div className="mb-6 rounded-md border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900">
            Historical snapshot from the audit trail. This view is read-only.
          </div>
        )}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="w-full flex mb-6 gap-x-2 gap-y-1 flex-wrap">
            <TabsTrigger value="information">
              Quality Event Information
            </TabsTrigger>
            <TabsTrigger value="nonconformity">
              Nonconformity Identification
            </TabsTrigger>
            <TabsTrigger value="plan">Action Plan</TabsTrigger>
            <TabsTrigger value="approvals">Approvals</TabsTrigger>
            {mode === DETAIL_MODE.STATIC && (
              <TabsTrigger value="summary">Review Summary</TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="information" className="mt-0 space-y-8">
            <QualityInformationView />
          </TabsContent>

          <TabsContent value="nonconformity" className="mt-0">
            <QualityNonconformityView />
          </TabsContent>

          <TabsContent value="plan" className="mt-0">
            <QualityPlanView />
          </TabsContent>

          <TabsContent value="approvals" className="mt-0 space-y-8">
            <QualityApprovalsView />
          </TabsContent>

          <TabsContent value="summary" className="mt-0">
            <CommentsPanel
              activeTab={activeTab}
              canSubmit={Boolean(
                !isSnapshotMode &&
                  reviewerRole &&
                  quality?.status === 'REVIEWING'
              )}
              className="mt-6"
              currentUserSub={getUserInfo()?.sub}
              entityId={id}
              entityType="QualityEvent"
              reviewerRole={reviewerRole}
              reviews={reviews}
              setCanApprove={setCanApprove}
              setReviews={setReviews}
              tabs={COMMENT_TABS}
            />
          </TabsContent>
        </Tabs>
        {showCommentsPanel && (
          <CommentsPanel
            activeTab={activeTab}
            canSubmit={Boolean(
              !isSnapshotMode &&
                reviewerRole &&
                quality?.status === 'REVIEWING'
            )}
            className="mt-6"
            currentUserSub={getUserInfo()?.sub}
            entityId={id}
            entityType="QualityEvent"
            reviewerRole={reviewerRole}
            reviews={reviews}
            setCanApprove={setCanApprove}
            setReviews={setReviews}
            tabs={COMMENT_TABS}
          />
        )}
      </PageLayout>
    </QualityDetailContext.Provider>
  );
};

export default QualityDetailPage;
